// src/pages/invoices/InvoiceDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import API_BASE_URL from "../../utils/config";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [firm, setFirm] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/invoices/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInvoice(res.data?.invoice || null);
        setItems(Array.isArray(res.data?.items) ? res.data.items : []);
        setFirm(res.data?.firm || null);
      } catch (e) {
        console.error(e);
        setError("Failed to load invoice.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, token]);

  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const fmtQty = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    const isIntLike = Math.abs(n - Math.round(n)) < 1e-9;
    return isIntLike ? String(Math.round(n)) : n.toFixed(2);
  };

  // MM/YYYY or "-"
  const fmtExp = (v) => {
    const s = v ? String(v) : "";
    if (!/\d/.test(s)) return "-";
    try {
      const mmYYYY = s.slice(0, 7).replace(/-/g, "/"); // "YYYY/MM"
      const parts = mmYYYY.split("/");
      if (parts.length === 2) return `${parts[1]}/${parts[0]}`; // "MM/YYYY"
      return mmYYYY;
    } catch {
      return "-";
    }
  };

  const invDate = useMemo(() => {
    const v = invoice?.invoice_date || invoice?.created_at;
    if (!v) return null;
    try {
      return new Date(v);
    } catch {
      return null;
    }
  }, [invoice]);

  // Totals
  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, it) => sum + Number(it.rate || 0) * Number(it.quantity || 0),
        0
      ),
    [items]
  );
  const totalGST = useMemo(
    () => items.reduce((s, it) => s + Number(it.gst_amount || 0), 0),
    [items]
  );
  const lineGrand = useMemo(
    () => items.reduce((s, it) => s + Number(it.total_amount || 0), 0),
    [items]
  );
  const discount = useMemo(() => {
    const a = Number(subtotal);
    const b = Number(lineGrand);
    const withGST = subtotal + totalGST;
    let d = 0;
    if (a > b) d = a - b;
    else d = Math.max(0, withGST - b);
    return Math.abs(d);
  }, [subtotal, lineGrand, totalGST]);
  const grandTotal = useMemo(() => lineGrand, [lineGrand]);

  /* ----------------------- PDF ----------------------- */

  const loadImageAsDataURL = async (url) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

const downloadPDF = async () => {
  if (!invoice) return;

  // ---------- helpers ----------
  const nf2 = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const Rs = (n) => `Rs ${nf2.format(Number(n || 0))}`;
  const n2 = (v) => Number((v ?? 0) || 0);
  const safe = (v, d = "-") => (v === null || v === undefined || String(v).trim() === "" ? d : String(v));
  const fmtQ = (n) => { const x = Number(n || 0); return Math.abs(x - Math.round(x)) < 1e-9 ? String(Math.round(x)) : x.toFixed(2); };
  const fmtExp = (v) => {
    const s = v ? String(v) : "";
    if (!/\d/.test(s)) return "-";
    try {
      const y_m = s.slice(0, 7).replace(/-/g, "/"); // YYYY/MM
      const [Y, M] = y_m.split("/");
      return M && Y ? `${M}/${Y}` : y_m;
    } catch { return "-"; }
  };
  const loadImageAsDataURL = async (url) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  const invDateVal = invoice?.invoice_date || invoice?.created_at;
  const invDate = invDateVal ? new Date(invDateVal) : null;

  // ---------- Buyer ----------
  const billName = invoice?.customer_name || invoice?.name || invoice?.customer || "-";
  const addrParts = [invoice?.customer_address, invoice?.customer_locality, invoice?.customer_city, invoice?.customer_pincode]
    .filter((x) => x && String(x).trim() !== "");
  const billAddr = addrParts.length ? addrParts.join(", ") : "-";
  const billPhone = invoice?.customer_phone || invoice?.contact || invoice?.phone || "-";
  const billDL    = invoice?.customer_dl_no || invoice?.dl_no || "-";
  const billGST   = invoice?.customer_gst || invoice?.gstin || invoice?.gst_number || invoice?.gst_no || "-";

  // ---------- Seller ----------
  const orgName = firm?.name || "WECARE INDIA";
  const orgAddr1 = safe(firm?.address);
  const orgAddr2 = [firm?.city].filter(Boolean).join(", ");
  const orgGST = safe(firm?.gst_number);
  const orgDL  = safe(firm?.drug_license);
  const bank1 = {
    name: safe(firm?.bank1_name),
    acc:  safe(firm?.bank1_account_number),
    ifsc: safe(firm?.bank1_ifsc),
    upi:  safe(firm?.bank1_upi),
    qr:   safe(firm?.bank1_scanner_image, ""),
  };
  const bank2 = {
    name: firm?.bank2_name ? safe(firm?.bank2_name) : null,
    acc:  firm?.bank2_account_number ? safe(firm?.bank2_account_number) : null,
    ifsc: firm?.bank2_ifsc ? safe(firm?.bank2_ifsc) : null,
    upi:  firm?.bank2_upi ? safe(firm?.bank2_upi) : null,
    qr:   firm?.bank2_scanner_image ? safe(firm?.bank2_scanner_image) : null,
  };

  // ---------- per-line calc ----------
  const lineCalc = (it) => {
    const qty = n2(it.quantity);
    const free = n2(it.free_qty);
    const rate = n2(it.rate);
    const discPct = n2(it.discount);
    const gstPct  = n2(it.gst_percent);

    const gross   = qty * rate;
    const discAmt = +(gross * (discPct / 100)).toFixed(2);
    const taxable = +(gross - discAmt).toFixed(2);
    const halfPct = +(gstPct / 2).toFixed(2);
    const cgst    = +(taxable * (halfPct / 100)).toFixed(2);
    const sgst    = +(taxable * (halfPct / 100)).toFixed(2);
    const total   = +(taxable + cgst + sgst).toFixed(2);

    return { qty, free, rate, discPct, gstPct, taxable, cgst, sgst, total };
  };

  // ---------- summaries ----------
  let sumItems = 0, sumQty = 0, sumFree = 0, sumTaxable = 0, sumDiscAmt = 0, sumCgst = 0, sumSgst = 0, sumTotal = 0;
  const slab = new Map();
  (items || []).forEach((it) => {
    const c = lineCalc(it);
    sumItems += 1;
    sumQty   += c.qty;
    sumFree  += c.free;
    sumTaxable += c.taxable;
    sumCgst  += c.cgst;
    sumSgst  += c.sgst;
    sumTotal += c.total;
    const gross = c.qty * c.rate;
    sumDiscAmt += +(gross - c.taxable).toFixed(2);
    const k = c.gstPct || 0;
    if (!slab.has(k)) slab.set(k, { taxable: 0, cgst: 0, sgst: 0 });
    const s = slab.get(k);
    s.taxable += c.taxable; s.cgst += c.cgst; s.sgst += c.sgst;
  });
  const roundedNet = Math.round(sumTotal);
  const roundOff   = +(roundedNet - sumTotal).toFixed(2);

  // ---------- PDF (clean header) ----------
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 8;
  const R = W - M;

  const invNo = invoice.invoice_no || invoice.id || "-";
  const invDateStr = invDate ? invDate.toLocaleDateString() : "-";

  doc.setTextColor(0, 0, 0);

  // Seller block (left)
  let y = 12;
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text((orgName || "").toUpperCase(), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
  if (orgAddr1) { y += 4; doc.text(orgAddr1, M, y); }
  if (orgAddr2) { y += 4; doc.text(orgAddr2, M, y); }
  if (firm?.mobile || firm?.telephone) {
    y += 4; doc.text(`Phone: ${[firm?.mobile, firm?.telephone].filter(Boolean).join(", ")}`, M, y);
  }
  if (orgGST) { y += 4; doc.text(`GST No: ${orgGST}`, M, y); }
  if (orgDL)  { y += 4; doc.text(`D.L. No.: ${orgDL}`, M, y); }

  // Title + meta (right)
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("TAX INVOICE", R, 10, { align: "right" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  doc.text(`Invoice No: ${invNo}`, R, 14, { align: "right" });
  doc.text(`Date: ${invDateStr}`, R, 18, { align: "right" });

  // Buyer block (right)
  let y2 = 24;
  const buyerBlockW = 86;
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5);
  doc.text(`Bill To: ${billName}`, R, y2, { align: "right" });
  y2 += 4;
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
  const addrLines = doc.splitTextToSize(safe(billAddr), buyerBlockW);
  addrLines.forEach((ln) => { doc.text(ln, R, y2, { align: "right" }); y2 += 4; });
  if (billPhone && billPhone !== "-") { doc.text(`Contact: ${billPhone}`, R, y2, { align: "right" }); y2 += 4; }
  if (billDL && billDL !== "-")       { doc.text(`DL No: ${billDL}`, R, y2, { align: "right" }); y2 += 4; }
  if (billGST && billGST !== "-")     { doc.text(`GST No: ${billGST}`, R, y2, { align: "right" }); y2 += 4; }

  // divider
  const tableStartY = Math.max(y, y2) + 4;
  doc.setDrawColor(200); doc.line(M, tableStartY - 2, R, tableStartY - 2);

  // Items table
  const head = [[ "SNO","QTY+FR","PACK","HSN","DESCRIPTION","BATCH NO.","EXP","M.R.P.","S.RATE","DIS%","GST","AMOUNT" ]];
  const body = (items || []).map((it, i) => {
    const qty = n2(it.quantity);
    const free = n2(it.free_qty);
    const rate = n2(it.rate);
    const discPct = n2(it.discount);
    const gstPct = n2(it.gst_percent);
    const gross = qty * rate;
    const taxable = +(gross - gross * (discPct / 100)).toFixed(2);
    const qtyFr = Number(free) > 0 ? `${fmtQ(qty)}+${fmtQ(free)}` : fmtQ(qty);
    const pack  = (it.pack || it.pack_size || it.uom || "-") + "";
    const hsn   = (it.hsn_code || it.hsn || "-") + "";
    const desc  = (it.product_name || "-") + "";
    const batch = (it.batch_no || "-") + "";
    const exp   = fmtExp(it.expiry || it.expiry_date || it.exp);
    const mrp   = n2(it.mrp);
    return [ i + 1, qtyFr, pack, hsn, desc, batch, exp, nf2.format(mrp), nf2.format(rate), nf2.format(discPct), nf2.format(gstPct), nf2.format(taxable) ];
  });

  autoTable(doc, {
    startY: tableStartY,
    head, body,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 7.0, cellPadding: 1.2, lineColor: [225,225,225], lineWidth: 0.35 },
    headStyles: { fontSize: 7.3, fillColor: [245,248,255], textColor: 30, lineColor: [210,210,210] },
    alternateRowStyles: { fillColor: [250,250,250] },
    columnStyles: {
      0: { cellWidth: 6,  halign: "right" },
      1: { cellWidth: 12, halign: "right" },
      2: { cellWidth: 12 },
      3: { cellWidth: 14 },
      4: { cellWidth: 48 },
      5: { cellWidth: 16 },
      6: { cellWidth: 16 },
      7: { cellWidth: 14, halign: "right" },
      8: { cellWidth: 14, halign: "right" },
      9: { cellWidth: 12, halign: "right" },
      10:{ cellWidth: 12, halign: "right" },
      11:{ cellWidth: 18, halign: "right" },
    },
    margin: { left: M, right: M, top: 6, bottom: 6 },
    didDrawPage: () => {
      // minimal repeating header
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("TAX INVOICE", R, 10, { align: "right" });
      doc.setFont("helvetica", "normal"); doc.setFontSize(8);
      doc.text(`Invoice No: ${invNo}`, R, 14, { align: "right" });
      doc.text(`Date: ${invDateStr}`, R, 18, { align: "right" });
      const page = doc.getNumberOfPages();
      doc.setFontSize(7.2); doc.setTextColor(110);
      doc.text(`Page ${page}`, M, doc.internal.pageSize.getHeight() - 4);
    },
  });

  // ===== 3-column footer band (all start at SAME Y) =====
  const footerY = doc.lastAutoTable.finalY + 3;   // << same startY for all three

  // 3-column layout (tweak these three widths if needed)
  const gap = 4;                // space between columns
  const leftW = 80;             // GST class table width
  const midW  = 38;             // Items/Qty/Free table width
  const rightW = 56;            // Totals text block width

  const leftX = M;
  const midX  = leftX + leftW + gap;
  const rightX = midX + midW + gap;

  // --- LEFT: GST Class table ---
  const classes = [0, 5, 12, 18, 28];
  const classRows = classes.map((pct) => {
    const v = slab.get(pct) || { taxable: 0, cgst: 0, sgst: 0 };
    return [ `GST ${pct}%`, nf2.format(v.taxable), nf2.format(v.sgst), nf2.format(v.cgst), nf2.format(v.cgst + v.sgst) ];
  });

  autoTable(doc, {
    startY: footerY,
    head: [["CLASS", "TOTAL", "SGST", "CGST", "TOTAL GST"]],
    body: classRows,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 7, cellPadding: 1 },
    headStyles: { fontSize: 7.2, fillColor: [245,248,255], textColor: 30 },
    margin: { left: leftX, right: W - (leftX + leftW) },
    tableWidth: leftW,
  });

  // --- MIDDLE: Items / Qty / Free table ---
  autoTable(doc, {
    startY: footerY,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 7, cellPadding: 1 },
    head: [["Items", ""]],
    headStyles: { fillColor: [245,248,255], textColor: 30, lineWidth: 0.35 },
    body: [
      ["Total Items", String(sumItems)],
      ["Total Qty",   nf2.format(sumQty)],
      ["Free Qty",    nf2.format(sumFree)],
    ],
    margin: { left: midX, right: W - (midX + midW) },
    tableWidth: midW,
    columnStyles: {
      0: { cellWidth: Math.max(10, midW - 12) },
      1: { cellWidth: 12, halign: "right" },
    },
  });

  // --- RIGHT: Totals block (text), aligned to same Y ---
  const labelX = rightX;     // left edge of totals label column
  const valX   = rightX + rightW; // right-aligned values inside rightW
  const line = (label, val, yOff = 0, bold = false) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(7.2);
    doc.text(label, labelX, footerY + yOff);
    doc.text(val, valX, footerY + yOff, { align: "right" });
  };

  // header
  doc.setFont("helvetica", "bold"); doc.setFontSize(8);
  doc.text("TOTAL", labelX, footerY);
  doc.text(nf2.format(sumTaxable), valX, footerY, { align: "right" });

  let yOff = 5.2;
  line("Less Discount", Rs(sumDiscAmt), yOff); yOff += 4.6;
  line("SGST PAYABLE",  Rs(sumSgst),    yOff); yOff += 4.6;
  line("CGST PAYABLE",  Rs(sumCgst),    yOff); yOff += 4.6;
  line("Round Off",     nf2.format(roundOff), yOff); yOff += 4.6;
  line("CREDIT NOTE",   Rs(0),          yOff); yOff += 4.6;

  doc.setFont("helvetica", "bold"); doc.setFontSize(8.2);
  doc.text("NET AMT", labelX, footerY + yOff);
  doc.text(nf2.format(roundedNet), valX, footerY + yOff, { align: "right" });
  yOff += 6.4;

  // Amount in words
  const toWords = (num) => {
    num = Math.round(Number(num || 0));
    if (num === 0) return "ZERO";
    const a = ["","ONE","TWO","THREE","FOUR","FIVE","SIX","SEVEN","EIGHT","NINE","TEN","ELEVEN","TWELVE","THIRTEEN","FOURTEEN","FIFTEEN","SIXTEEN","SEVENTEEN","EIGHTEEN","NINETEEN"];
    const b = ["","","TWENTY","THIRTY","FORTY","FIFTY","SIXTY","SEVENTY","EIGHTY","NINETY"];
    const c = ["CRORE","LAKH","THOUSAND","HUNDRED"];
    const seg = [
      Math.floor(num / 10000000),
      Math.floor((num / 100000) % 100),
      Math.floor((num / 1000) % 100),
      Math.floor((num / 100) % 10),
      Math.floor(num % 100),
    ];
    const part = [];
    const two = (n) => (n < 20 ? a[n] : b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : ""));
    if (seg[0]) part.push(two(seg[0]) + " " + c[0]);
    if (seg[1]) part.push(two(seg[1]) + " " + c[1]);
    if (seg[2]) part.push(two(seg[2]) + " " + c[2]);
    if (seg[3]) part.push(a[seg[3]] + " " + c[3]);
    if (seg[4]) part.push((part.length ? "AND " : "") + two(seg[4]));
    return part.join(" ").replace(/\s+/g, " ").trim();
  };

  const words = `[RUPEES ${toWords(roundedNet)} ONLY]`;
  const wordsY = Math.max(doc.lastAutoTable.finalY, footerY + yOff) + 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.2);
  doc.text(words, M, wordsY);

  // Bank / QR
  let bottomY = wordsY + 6;
  const printBank = (title, b, yStart) => {
    if (!b?.name && !b?.acc && !b?.ifsc && !b?.upi) return yStart;
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.2);
    doc.text(title, M, yStart);
    doc.setFont("helvetica", "normal");
    let yb = yStart + 4.8;
    if (b?.name) { doc.text(`${b.name}`, M, yb); yb += 4.2; }
    if (b?.acc || b?.ifsc) { doc.text(`A/c: ${b.acc || "-"}     IFSC: ${b.ifsc || "-"}`, M, yb); yb += 4.2; }
    if (b?.upi) { doc.text(`UPI: ${b.upi}`, M, yb); yb += 4.2; }
    return yb;
  };

  bottomY = printBank("Bank 1:", bank1, bottomY + 1.5);
  if (bank2?.name || bank2?.acc || bank2?.ifsc || bank2?.upi) {
    bottomY = printBank("Bank 2:", bank2, bottomY + 2);
  }

  if (bank1.qr) {
    const dataURL = await loadImageAsDataURL(bank1.qr);
    if (dataURL) {
      const qrW = 26, qrH = 26;
      const qrX = R - qrW;
      const qrY = Math.max(wordsY - 2, 18);
      try {
        doc.addImage(dataURL, "PNG", qrX, qrY, qrW, qrH);
        doc.setFontSize(6.8);
        doc.text("Scan to Pay (UPI)", qrX + qrW / 2, qrY + qrH + 4, { align: "center" });
      } catch {}
    }
  }

  const jur = `All Subject to ${firm?.city || "BHILWARA"} Jurisdiction only.    E & O.E.`;
  const sigY = Math.max(bottomY + 6, wordsY + 34);
  doc.text(jur, M, sigY);
  doc.text(`For ${(orgName || "").toUpperCase()}`, R, sigY, { align: "right" });
  doc.text("Authorised Signatory", R, sigY + 6, { align: "right" });

  doc.save(`Invoice_${invNo}.pdf`);
};


  /* ----------------------- UI ----------------------- */

  const modifyInvoice = () => {
    if (!invoice) return;
    navigate(`/invoices/${invoice.id}/edit`);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-5xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          Loading invoice…
        </div>
      </div>
    );
  }
  if (error || !invoice) {
    return (
      <div className="p-6">
        <div className="mx-auto max-w-5xl rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700 shadow-sm">
          {error || "Invoice not found"}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header Card */}
      <div className="mx-auto max-w-5xl rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 bg-gradient-to-r from-blue-900 to-blue-700 px-4 py-4 text-white sm:px-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs/relaxed text-white/70">
                {(firm?.name || "WECARE INDIA") + " Agency"}
              </div>
              <h1 className="text-xl font-semibold">
                Invoice #{invoice.invoice_no || invoice.id}
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={downloadPDF}
                className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-blue-700 shadow-sm hover:bg-blue-50"
              >
                Download PDF
              </button>
              <button
                onClick={modifyInvoice}
                className="rounded-lg bg-yellow-400 px-3 py-2 text-sm font-medium text-blue-900 shadow-sm hover:bg-yellow-300"
              >
                Modify Invoice
              </button>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 sm:p-6">
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-xs text-slate-500">Bill To</div>
            <div className="mt-1 font-medium text-slate-900">
              {invoice.customer_name ||
                invoice.name ||
                `Customer ID: ${invoice.customer_id || "-"}`}
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {[invoice?.address, invoice?.city].filter(Boolean).join(", ")}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="text-slate-500">Invoice Date</div>
              <div className="text-right text-slate-900">
                {invDate ? invDate.toLocaleDateString() : "-"}
              </div>
              <div className="text-slate-500">Order ID</div>
              <div className="text-right text-slate-900">
                {invoice.order_id || "-"}
              </div>
            </div>
          </div>
        </div>

        {/* Totals cards */}
        <div className="grid grid-cols-2 gap-3 px-4 pb-4 sm:grid-cols-4 sm:px-6">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <div className="text-xs text-slate-500">Items</div>
            <div className="text-lg font-semibold">{items.length}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <div className="text-xs text-slate-500">Subtotal</div>
            <div className="text-lg font-semibold tabular-nums">
              {fmtINR(subtotal)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <div className="text-xs text-slate-500">Discount</div>
            <div className="text-lg font-semibold tabular-nums">
              {fmtINR(discount)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
            <div className="text-xs text-slate-500">Grand Total</div>
            <div className="text-lg font-semibold tabular-nums">
              {fmtINR(grandTotal)}
            </div>
          </div>
        </div>

        {/* Items table (desktop) */}
        <div className="hidden overflow-x-auto px-4 pb-6 sm:block sm:px-6">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Product</th>
                <th className="px-3 py-2 text-left font-medium">HSN</th>
                <th className="px-3 py-2 text-left font-medium">Pack</th>
                <th className="px-3 py-2 text-left font-medium">Batch</th>
                <th className="px-3 py-2 text-left font-medium">Expiry</th>
                <th className="px-3 py-2 text-right font-medium">Qty+Fr</th>
                <th className="px-3 py-2 text-right font-medium">Rate</th>
                <th className="px-3 py-2 text-right font-medium">Disc%</th>
                <th className="px-3 py-2 text-right font-medium">GST%</th>
                <th className="px-3 py-2 text-right font-medium">GST Amt</th>
                <th className="px-3 py-2 text-right font-medium">Line Total</th>
              </tr>
            </thead>
            <tbody className="[&>tr:nth-child(even)]:bg-slate-50">
              {items.map((it, idx) => (
                <tr
                  key={`${it.product_id || idx}-${idx}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-3 py-2">{it.product_name}</td>
                  <td className="px-3 py-2">{it.hsn_code || it.hsn || "-"}</td>
                  <td className="px-3 py-2">{it.pack || it.pack_size || it.uom || "-"}</td>
                  <td className="px-3 py-2">{it.batch_no || "-"}</td>
                  <td className="px-3 py-2">{fmtExp(it.expiry || it.expiry_date || it.exp)}</td>
                  <td className="px-3 py-2 text-right">
                    {fmtQty(it.quantity)}
                    {Number(it.free_qty) > 0 ? ` + ${fmtQty(it.free_qty)}` : ""}
                  </td>
                  <td className="px-3 py-2 text-right">{fmtINR(it.rate)}</td>
                  <td className="px-3 py-2 text-right">
                    {Number(it.discount || 0)}%
                  </td>
                  <td className="px-3 py-2 text-right">
                    {Number(it.gst_percent || 0)}%
                  </td>
                  <td className="px-3 py-2 text-right">
                    {fmtINR(it.gst_amount)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {fmtINR(it.total_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Items list (mobile) */}
        <div className="space-y-3 px-4 pb-6 sm:hidden">
          {items.map((it, idx) => (
            <div
              key={`${it.product_id || idx}-m`}
              className="rounded-xl border border-slate-200 p-3"
            >
              <div className="font-medium text-slate-900">
                {it.product_name}
              </div>
              <div className="mt-1 grid grid-cols-2 gap-2 text-sm">
                <div className="text-slate-500">HSN</div>
                <div className="text-right">{it.hsn_code || it.hsn || "-"}</div>

                <div className="text-slate-500">Pack</div>
                <div className="text-right">{it.pack || it.pack_size || it.uom || "-"}</div>

                <div className="text-slate-500">Batch</div>
                <div className="text-right">{it.batch_no || "-"}</div>

                <div className="text-slate-500">Expiry</div>
                <div className="text-right">{fmtExp(it.expiry || it.expiry_date || it.exp)}</div>

                <div className="text-slate-500">Qty / Free</div>
                <div className="text-right">
                  {fmtQty(it.quantity)} / {fmtQty(it.free_qty)}
                </div>

                <div className="text-slate-500">Rate</div>
                <div className="text-right">{fmtINR(it.rate)}</div>

                <div className="text-slate-500">Disc%</div>
                <div className="text-right">{Number(it.discount || 0)}%</div>

                <div className="text-slate-500">GST%</div>
                <div className="text-right">{Number(it.gst_percent || 0)}%</div>

                <div className="text-slate-500">GST Amt</div>
                <div className="text-right">{fmtINR(it.gst_amount)}</div>

                <div className="text-slate-500">Line Total</div>
                <div className="text-right font-semibold">
                  {fmtINR(it.total_amount)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Grand totals (bottom) */}
        <div className="border-t border-slate-100 px-4 py-4 sm:px-6">
          <div className="ml-auto w-full max-w-sm">
            <div className="flex items-center justify-between text-sm">
              <div className="text-slate-600">Subtotal</div>
              <div className="tabular-nums">{fmtINR(subtotal)}</div>
            </div>
            <div className="mt-1 flex items-center justify-between text-sm">
              <div className="text-slate-600">Discount</div>
              <div className="tabular-nums">{fmtINR(discount)}</div>
            </div>
            <div className="mt-1 flex items-center justify-between text-sm">
              <div className="text-slate-600">Total GST</div>
              <div className="tabular-nums">{fmtINR(totalGST)}</div>
            </div>
            <div className="mt-2 flex items-center justify-between text-base font-semibold">
              <div>Grand Total</div>
              <div className="tabular-nums">{fmtINR(grandTotal)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Back */}
      <div className="mx-auto mt-4 max-w-5xl text-right">
        <button
          onClick={() => navigate(-1)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          Back
        </button>
      </div>
    </div>
  );
}
